'use client';

import { Loader2, LocateFixed, MapPin, Search } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';

import { KeywordSuggestionsDropdown } from '@website/components/KeywordSuggestionsDropdown';
import type { RecentLocation } from '@website/components/LocationPicker/types';
import { useLocationSearch } from '@website/hooks/useLocationSearch';
import { useSuggestions } from '@website/hooks/useSuggestions';

interface HomeSearchBarProps {
  className?: string;
  onSearch: (query: string, location?: { latitude: number; longitude: number; name: string }) => void;
  locationLabel?: string;
}

export function HomeSearchBar({ className, onSearch, locationLabel }: HomeSearchBarProps) {
  const t = useTranslations();
  const locale = useLocale();
  const [keyword, setKeyword] = useState('');
  const [debouncedKeyword, setDebouncedKeyword] = useState('');
  const [locationInput, setLocationInput] = useState(locationLabel || '');
  const [showDropdown, setShowDropdown] = useState(false);
  const [showKeywordDropdown, setShowKeywordDropdown] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedLocationRef = useRef<RecentLocation | null>(null);
  const setSelectedLocation = (loc: RecentLocation | null) => {
    selectedLocationRef.current = loc;
  };

  useEffect(() => {
    const id = setTimeout(() => setDebouncedKeyword(keyword), 150);
    return () => clearTimeout(id);
  }, [keyword]);

  const { data: keywordSuggestions = [], isFetching: isFetchingSuggestions } = useSuggestions(debouncedKeyword, locale);

  const {
    searchQuery,
    setSearchQuery,
    searchResults,
    isSearching,
    recentLocations,
    saveRecentLocation,
    getCurrentLocation,
    isLoadingCurrentLocation
  } = useLocationSearch();

  useEffect(() => {
    if (locationLabel) setLocationInput(locationLabel);
  }, [locationLabel]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
        setShowKeywordDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLocationInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocationInput(value);
    setSearchQuery(value);
    setSelectedLocation(null);
    setShowDropdown(true);
    setShowKeywordDropdown(false);
  };

  const handleLocationSelect = useCallback(
    (location: RecentLocation) => {
      setLocationInput(location.name);
      setSelectedLocation(location);
      saveRecentLocation(location);
      setShowDropdown(false);
    },
    [saveRecentLocation]
  );

  const handleUseCurrentLocation = useCallback(async () => {
    const location = await getCurrentLocation();
    if (location) {
      setLocationInput(location.name);
      setSelectedLocation(location);
      setShowDropdown(false);
    }
  }, [getCurrentLocation]);

  const submitSearch = useCallback(
    (keywordValue: string) => {
      const loc = selectedLocationRef.current;
      if (!keywordValue.trim() && !loc) return;
      onSearch(
        keywordValue.trim(),
        loc ? { latitude: loc.latitude, longitude: loc.longitude, name: loc.name } : undefined
      );
    },
    [onSearch]
  );

  const handleSubmit = () => submitSearch(keyword);

  const handleKeywordSelect = useCallback(
    (item: { name: string }) => {
      setKeyword(item.name);
      setShowKeywordDropdown(false);
      submitSearch(item.name);
    },
    [submitSearch]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setShowDropdown(false);
      setShowKeywordDropdown(false);
      handleSubmit();
    }
  };

  // Determine dropdown items
  const hasQuery = searchQuery.trim().length >= 2;
  const suggestions = hasQuery ? searchResults : recentLocations;

  const locationDropdown = showDropdown && (
    <div className="absolute top-full left-0 right-0 md:w-[320px] mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg z-50 max-h-[280px] overflow-y-auto">
      <button
        type="button"
        className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-neutral-50 transition-colors text-left border-b border-neutral-100"
        onClick={handleUseCurrentLocation}
        disabled={isLoadingCurrentLocation}
      >
        {isLoadingCurrentLocation ? (
          <Loader2 className="w-4 h-4 text-primary-500 shrink-0 animate-spin" />
        ) : (
          <LocateFixed className="w-4 h-4 text-primary-500 shrink-0" />
        )}
        <span className="text-sm font-medium text-primary-500">{t('SearchPage.useCurrentLocation')}</span>
      </button>
      {isSearching ? (
        <div className="px-4 py-3 text-sm text-neutral-500">{t('SearchPage.searching')}</div>
      ) : suggestions.length > 0 ? (
        suggestions.map((location) => (
          <button
            key={location.id}
            type="button"
            className="w-full flex items-start gap-2 px-4 py-2.5 hover:bg-neutral-50 transition-colors text-left"
            onClick={() => handleLocationSelect(location)}
          >
            <MapPin className="w-4 h-4 text-neutral-400 mt-0.5 shrink-0" />
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-medium text-neutral-800 truncate">{location.name}</span>
              {location.description !== location.name && (
                <span className="text-xs text-neutral-500 truncate">{location.description}</span>
              )}
            </div>
          </button>
        ))
      ) : hasQuery ? (
        <div className="px-4 py-3 text-sm text-neutral-500">{t('SearchPage.noLocationsFound')}</div>
      ) : null}
    </div>
  );

  return (
    <div ref={containerRef} className={`relative z-10 w-full max-w-[576px] ${className || ''}`}>
      {/* Mobile: stacked vertical layout */}
      <div className="flex flex-col gap-3 md:hidden">
        <div className="relative">
          <div className="flex items-center gap-2 p-3 rounded-lg border border-neutral-200 bg-white transition-colors hover:border-primary-500 focus-within:border-primary-500">
            <MapPin className="w-5 h-5 text-neutral-600 shrink-0" />
            <input
              type="text"
              placeholder={t('SearchPage.chooseLocation')}
              className="bg-transparent outline-none text-base font-medium text-neutral-600 w-full placeholder:text-neutral-600"
              value={locationInput}
              onChange={handleLocationInputChange}
              onFocus={() => {
                setShowDropdown(true);
                setShowKeywordDropdown(false);
              }}
              onKeyDown={handleKeyDown}
            />
          </div>
          {locationDropdown}
        </div>

        <div className="relative">
          <div className="flex items-center gap-2 p-3 rounded-lg border border-neutral-200 bg-white transition-colors hover:border-primary-500 focus-within:border-primary-500">
            <Search className="w-5 h-5 text-neutral-600 shrink-0" />
            <input
              type="text"
              placeholder={t('SearchPage.searchPlaceholder')}
              className="bg-transparent outline-none text-base font-medium text-neutral-600 w-full placeholder:text-neutral-600"
              value={keyword}
              onChange={(e) => {
                setKeyword(e.target.value);
                setShowKeywordDropdown(true);
              }}
              onFocus={() => {
                setShowKeywordDropdown(true);
                setShowDropdown(false);
              }}
              onKeyDown={handleKeyDown}
            />
            <button
              type="button"
              onClick={handleSubmit}
              aria-label={t('SearchPage.searchPlaceholder')}
              className="flex items-center justify-center w-9 h-9 rounded-md bg-primary-500 hover:bg-primary-600 transition-colors shrink-0"
            >
              <Search className="w-4 h-4 text-white" />
            </button>
          </div>
          {showKeywordDropdown && (
            <KeywordSuggestionsDropdown
              query={keyword}
              suggestions={keywordSuggestions}
              isLoading={isFetchingSuggestions}
              onSelect={handleKeywordSelect}
            />
          )}
        </div>
      </div>

      {/* Desktop: single unified container */}
      <div className="hidden md:flex items-center h-[46px] rounded-lg border border-neutral-200 bg-white transition-colors hover:border-primary-500 focus-within:border-primary-500">
        <div className="relative flex items-center gap-2 px-3 shrink-0 w-[200px] h-full">
          <MapPin className="w-5 h-5 text-neutral-600 shrink-0" />
          <input
            type="text"
            placeholder={t('SearchPage.chooseLocation')}
            className="bg-transparent outline-none text-sm font-medium text-neutral-600 w-full placeholder:text-neutral-600"
            value={locationInput}
            onChange={handleLocationInputChange}
            onFocus={() => {
              setShowDropdown(true);
              setShowKeywordDropdown(false);
            }}
            onKeyDown={handleKeyDown}
          />
          {locationDropdown}
        </div>

        <div className="w-px h-5 bg-neutral-300 shrink-0" />

        <div className="relative flex items-center gap-2 px-3 flex-1 h-full">
          <Search className="w-4 h-4 text-neutral-600 shrink-0" />
          <input
            type="text"
            placeholder={t('SearchPage.searchPlaceholder')}
            className="bg-transparent outline-none text-sm font-medium text-neutral-600 w-full placeholder:text-neutral-600"
            value={keyword}
            onChange={(e) => {
              setKeyword(e.target.value);
              setShowKeywordDropdown(true);
            }}
            onFocus={() => {
              setShowKeywordDropdown(true);
              setShowDropdown(false);
            }}
            onKeyDown={handleKeyDown}
          />
          {showKeywordDropdown && (
            <KeywordSuggestionsDropdown
              query={keyword}
              suggestions={keywordSuggestions}
              isLoading={isFetchingSuggestions}
              onSelect={handleKeywordSelect}
            />
          )}
        </div>

        <button
          type="button"
          onClick={handleSubmit}
          aria-label={t('SearchPage.searchPlaceholder')}
          className="flex items-center justify-center w-9 h-9 mr-1.5 rounded-md bg-primary-500 hover:bg-primary-600 transition-colors shrink-0"
        >
          <Search className="w-4 h-4 text-white" />
        </button>
      </div>
    </div>
  );
}
